import React, { useEffect, useState } from 'react';
import { Place } from '../types';
import { getPlaceDetails } from '../services/routexApi';

interface PlaceDetailsModalProps {
  place: Place | null;
  isOpen: boolean;
  onClose: () => void;
  onRemove: (placeId: string) => void;
}

interface PlaceDetails {
  name: string;
  formatted_address?: string;
  rating?: number;
  user_ratings_total?: number;
  photos?: string[]; // Array of photo URLs
  reviews?: Array<{
    author_name: string;
    rating: number;
    text: string;
    relative_time_description: string;
  }>;
  url?: string;
  opening_hours?: {
    open_now?: boolean;
    weekday_text?: string[];
  };
}

export const PlaceDetailsModal: React.FC<PlaceDetailsModalProps> = ({
  place,
  isOpen,
  onClose,
  onRemove,
}) => {
  const [details, setDetails] = useState<PlaceDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && place) {
      fetchPlaceDetails(place.place_id);
    } else {
      setDetails(null);
      setError(null);
    }
  }, [isOpen, place]);

  const fetchPlaceDetails = async (placeId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedDetails = await getPlaceDetails({
        place_id: placeId,
        name: place?.name,
        location: place?.location,
      });
      setDetails(fetchedDetails);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to load place details');
      // Fallback to basic place info
      if (place) {
        setDetails({
          name: place.name,
          formatted_address: place.formatted_address,
          rating: place.rating,
          user_ratings_total: place.user_ratings_total,
          photos: [],
          reviews: [],
        });
      }
    }
  };

  if (!isOpen || !place) return null;

  const displayDetails = details || {
    name: place.name,
    formatted_address: place.formatted_address,
    rating: place.rating,
    user_ratings_total: place.user_ratings_total,
    photos: [],
    reviews: [],
    opening_hours: place.opening_hours,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Place Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Place Name and Address */}
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{displayDetails.name}</h3>
                {displayDetails.formatted_address && (
                  <p className="text-gray-600 text-sm">{displayDetails.formatted_address}</p>
                )}
              </div>

              {/* Rating */}
              {displayDetails.rating && (
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      <span className="text-yellow-400 text-xl">⭐</span>
                      <span className="text-lg font-semibold text-gray-900 ml-1">
                        {displayDetails.rating.toFixed(1)}
                      </span>
                    </div>
                    {displayDetails.user_ratings_total && (
                      <span className="text-gray-500 text-sm">
                        ({displayDetails.user_ratings_total.toLocaleString()} reviews)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Opening hours */}
              {displayDetails.opening_hours && (
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-gray-600 text-lg">schedule</span>
                    Opening hours
                  </h4>
                  {typeof displayDetails.opening_hours.open_now === 'boolean' && (
                    <p className={`text-sm font-medium mb-2 ${displayDetails.opening_hours.open_now ? 'text-green-600' : 'text-gray-600'}`}>
                      {displayDetails.opening_hours.open_now ? 'Open now' : 'Closed'}
                    </p>
                  )}
                  {displayDetails.opening_hours.weekday_text && displayDetails.opening_hours.weekday_text.length > 0 && (
                    <ul className="text-sm text-gray-600 space-y-0.5">
                      {displayDetails.opening_hours.weekday_text.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Photos */}
              {displayDetails.photos && displayDetails.photos.length > 0 && (
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Photos</h4>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {displayDetails.photos.map((photoUrl, index) => (
                      <img
                        key={index}
                        src={photoUrl}
                        alt={`${displayDetails.name} photo ${index + 1}`}
                        className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => {
                          // Hide broken images
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Reviews */}
              {displayDetails.reviews && displayDetails.reviews.length > 0 && (
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Reviews</h4>
                  <div className="space-y-4">
                    {displayDetails.reviews.slice(0, 3).map((review, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">{review.author_name}</span>
                          <div className="flex items-center">
                            <span className="text-yellow-400 text-sm">⭐</span>
                            <span className="text-sm text-gray-700 ml-1">{review.rating}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {review.relative_time_description}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{review.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="p-4 bg-yellow-50 border-b border-gray-200">
                  <p className="text-sm text-yellow-800">{error}</p>
                </div>
              )}

              {/* Google Maps Link */}
              {displayDetails.url && (
                <div className="p-4 border-b border-gray-200">
                  <a
                    href={displayDetails.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    <span className="material-symbols-outlined text-[20px]">open_in_new</span>
                    View on Google Maps
                  </a>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with Remove Button */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => {
              onRemove(place.place_id);
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">delete</span>
            <span>Remove from Route</span>
          </button>
        </div>
      </div>
    </div>
  );
};
